import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export interface PostalAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

type ConfirmationResult<T extends PostalAddress> =
  | (T & { addressValidationToken: string })
  | null;

type ConfirmAddress = <T extends PostalAddress>(
  address: T,
) => Promise<ConfirmationResult<T>>;

const AddressConfirmationContext = createContext<ConfirmAddress | null>(null);
const POSTAL_FIELDS: (keyof PostalAddress)[] = [
  "street",
  "city",
  "state",
  "zip",
  "country",
];

interface ActiveConfirmation {
  key: string;
  promise: Promise<ConfirmationResult<PostalAddress>>;
  resolve: (value: ConfirmationResult<PostalAddress>) => void;
  reject: (error: Error) => void;
  original: PostalAddress;
  suggested?: PostalAddress;
  token?: string;
  restoreFocusTo: HTMLElement | null;
  abortController: AbortController;
}

function postalAddress(address: PostalAddress): PostalAddress {
  return {
    street: address.street,
    city: address.city,
    state: address.state,
    zip: address.zip,
    country: address.country,
  };
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => element.getAttribute("aria-hidden") !== "true");
}

function AddressComparison({
  label,
  address,
  emphasized,
}: {
  label: string;
  address: PostalAddress;
  emphasized?: boolean;
}) {
  return (
    <section
      aria-label={label}
      className={`min-w-0 rounded-lg border p-4 ${
        emphasized
          ? "border-emerald-300 bg-emerald-50/70"
          : "border-slate-200 bg-slate-50"
      }`}
    >
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
        {label}
      </h3>
      <address className="not-italic text-sm leading-6 text-slate-800 [overflow-wrap:anywhere]">
        <span className="block">{address.street}</span>
        <span className="block">
          {address.city}, {address.state} {address.zip}
        </span>
        <span className="block">{address.country}</span>
      </address>
    </section>
  );
}

function ConfirmationDialog({
  original,
  suggested,
  onConfirm,
  onCancel,
}: {
  original: PostalAddress;
  suggested: PostalAddress;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusable = getFocusableElements(dialog);
    (focusable[0] ?? dialog).focus();
  }, []);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onCancel();
      return;
    }
    if (event.key !== "Tab") return;

    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusable = getFocusableElements(dialog);
    if (focusable.length === 0) {
      event.preventDefault();
      dialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center overflow-y-auto bg-slate-950/55 p-3 sm:p-6">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="address-confirmation-title"
        aria-describedby="address-confirmation-description"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className="my-auto max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-2xl sm:max-h-[calc(100dvh-3rem)]"
      >
        <div className="border-b border-slate-200 px-5 py-5 sm:px-7">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-emerald-800">
            Address check
          </p>
          <h2 id="address-confirmation-title" className="text-xl font-semibold tracking-tight text-slate-900">
            Confirm the suggested address
          </h2>
          <p id="address-confirmation-description" className="mt-2 text-sm leading-6 text-slate-600">
            Review the address correction before continuing. The submitted address cannot be used without validation.
          </p>
        </div>
        <div className="grid gap-3 px-5 py-5 sm:grid-cols-2 sm:px-7">
          <AddressComparison label="You entered" address={original} />
          <AddressComparison label="Suggested address" address={suggested} emphasized />
        </div>
        <p className="mx-5 mb-4 rounded-md border-l-2 border-amber-500 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-950 sm:mx-7">
          Postal validation does not approve a shipping location. Staff must still verify customer shipping requests.
        </p>
        <p className="px-5 pb-4 text-xs text-slate-500 sm:px-7">
          Address suggestion content provided by Google Maps.
        </p>
        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50/70 px-5 py-4 sm:flex-row sm:justify-end sm:px-7">
          <button
            type="button"
            onClick={onCancel}
            className="min-h-11 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:ring-offset-2"
          >
            Edit address
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="min-h-11 rounded-lg bg-emerald-800 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-900 focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:ring-offset-2"
          >
            Use suggested address
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function AddressConfirmationProvider({ children }: { children: ReactNode }) {
  const [dialog, setDialog] = useState<{
    original: PostalAddress;
    suggested: PostalAddress;
  } | null>(null);
  const activeRef = useRef<ActiveConfirmation | null>(null);
  const mountedRef = useRef(false);

  const finish = useCallback(
    (confirmation: ActiveConfirmation, value: ConfirmationResult<PostalAddress>) => {
      if (activeRef.current !== confirmation) return;
      activeRef.current = null;
      confirmation.abortController.abort();
      if (mountedRef.current) setDialog(null);
      confirmation.resolve(value);
      if (confirmation.restoreFocusTo?.isConnected) confirmation.restoreFocusTo.focus();
    },
    [],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const active = activeRef.current;
      if (active) {
        activeRef.current = null;
        active.abortController.abort();
        active.resolve(null);
      }
    };
  }, []);

  const confirmAddress = useCallback<ConfirmAddress>((address) => {
    const submitted = postalAddress(address);
    const key = JSON.stringify(address);
    const active = activeRef.current;
    if (active) {
      if (active.key === key) {
        return active.promise as Promise<ConfirmationResult<typeof address>>;
      }
      return Promise.reject(
        new Error("Another address confirmation is already in progress. Finish or cancel it before checking a different address."),
      );
    }

    const restoreFocusTo =
      typeof document !== "undefined" && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    let resolvePromise!: (value: ConfirmationResult<PostalAddress>) => void;
    let rejectPromise!: (error: Error) => void;
    const promise = new Promise<ConfirmationResult<PostalAddress>>((resolve, reject) => {
      resolvePromise = resolve;
      rejectPromise = reject;
    });
    const confirmation: ActiveConfirmation = {
      key,
      promise,
      resolve: resolvePromise,
      reject: rejectPromise,
      // Retain caller-owned metadata in the returned value; only the request body is
      // deliberately narrowed to postal fields.
      original: address,
      restoreFocusTo,
      abortController: new AbortController(),
    };
    activeRef.current = confirmation;

    void (async () => {
      try {
        const response = await fetch("/api/shipping-addresses/validate", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(submitted),
          signal: confirmation.abortController.signal,
        });
        if (!mountedRef.current || activeRef.current !== confirmation) return;

        let payload: { suggested?: PostalAddress; addressValidationToken?: string; error?: string };
        try {
          payload = await response.json();
        } catch {
          payload = {};
        }
        if (!response.ok) {
          throw new Error(
            payload.error ||
              "We could not validate this address. Check the address and try again, or contact support.",
          );
        }
        if (
          !payload.suggested ||
          !payload.addressValidationToken ||
          !POSTAL_FIELDS.every((field) => typeof payload.suggested?.[field] === "string")
        ) {
          throw new Error(
            "Address validation returned an incomplete suggestion. Please try again or contact support.",
          );
        }

        confirmation.suggested = postalAddress(payload.suggested);
        confirmation.token = payload.addressValidationToken;
        setDialog({ original: submitted, suggested: confirmation.suggested });
      } catch (error) {
        if (activeRef.current !== confirmation) return;
        activeRef.current = null;
        if (mountedRef.current) setDialog(null);
        // Rejections are surfaced to the caller so the form can show an actionable error.
        confirmation.reject(
          error instanceof Error
            ? error
            : new Error("Address validation failed. Please try again or contact support."),
        );
      }
    })();

    return promise as Promise<ConfirmationResult<typeof address>>;
  }, []);

  const chooseSuggestedAddress = useCallback(() => {
    const active = activeRef.current;
    if (!active?.suggested || !active.token) return;
    const result = {
      ...(active.original as PostalAddress),
      ...active.suggested,
      addressValidationToken: active.token,
    };
    finish(active, result);
  }, [finish]);

  const cancelConfirmation = useCallback(() => {
    const active = activeRef.current;
    if (active) finish(active, null);
  }, [finish]);

  return (
    <AddressConfirmationContext.Provider value={confirmAddress}>
      {children}
      {dialog && (
        <ConfirmationDialog
          original={dialog.original}
          suggested={dialog.suggested}
          onConfirm={chooseSuggestedAddress}
          onCancel={cancelConfirmation}
        />
      )}
    </AddressConfirmationContext.Provider>
  );
}

export function useAddressConfirmation(): ConfirmAddress {
  const context = useContext(AddressConfirmationContext);
  if (!context) {
    throw new Error("useAddressConfirmation must be used within AddressConfirmationProvider.");
  }
  return context;
}
